<script setup lang="ts">
import { computed } from 'vue';

import type { VFormFilterButtonProps } from '@modules/form-widgets/components/VFormFilterButton/types';
import { TRANSLATION_GROUP_AFFILIATE_PROGRAM_COMMISSIONS } from '@modules/affiliate-program-commissions/constants';
import { useDevice } from '@modules/core/composables/device';
import { useConfirmDialogs } from '@modules/core/composables/dialogs';
import VFormButtonsGroupWidget from '@modules/form-widgets/components/VFormButtonsGroupWidget/VFormButtonsGroupWidget.vue';
import { useI18n } from '@modules/i18n/composables';

import type {
  CommissionCountriesTabsEmits,
  CommissionCountriesTabsProps,
} from './types';
import CommissionCountriesMobileTabs from '../CommissionCountriesMobileTabs/CommissionCountriesMobileTabs.vue';

const props = withDefaults(defineProps<CommissionCountriesTabsProps>(), {
  translateKey: TRANSLATION_GROUP_AFFILIATE_PROGRAM_COMMISSIONS,
});
const emit = defineEmits<CommissionCountriesTabsEmits>();

const { confirmDelete } = useConfirmDialogs();
const { getTranslation } = useI18n();
const isGreaterThenSm = useDevice().breakpoints.sm;

const buttons = computed<VFormFilterButtonProps[]>(() => (
  props.tabs.map((item) => ({
    type: item.isActive ? 'secondary' : 'transparent-secondary',
    allowExpand: item.index !== 0,
    expanded: item.expanded,
    label: item.label || item.emptyLabel,
    allowRemove: item.index !== 0,
    isClickable: !item.isActive,
  } as VFormFilterButtonProps))
));

function onToggle(index: number): void {
  const tab = props.tabs[index];

  if (tab) {
    emit('expand-toggle', tab.index);
  }
}

function onRemove(index: number): void {
  const tab = props.tabs[index];

  if (tab) {
    confirmDelete({
      group: 'confirmDialog',
      header: getTranslation(props.translateKey, 'DIALOG_FILTER_TITLE').value,
      message: getTranslation(props.translateKey, 'DIALOG_FILTER_CONTENT', {
        name: tab.label || tab.emptyLabel,
      }).value,
      acceptLabel: getTranslation(props.translateKey, 'DIALOG_FILTER_YES').value,
      rejectLabel: getTranslation(props.translateKey, 'DIALOG_FILTER_NO').value,
      accept: () => {
        emit('remove', tab.index);
      },
    });
  }
}

function onSelectItem(index: number): void {
  const tab = props.tabs[index];

  if (tab) {
    emit('select', tab.index);
  }
}
</script>

<template>
  <VFormButtonsGroupWidget v-data-qa-auto-id="'CommissionCountriesTabs'"
    v-if="isGreaterThenSm"
    :buttons="buttons"
    :add-label="$getTranslation(translateKey, 'FILTER_ADD_NEW_FILTER')"
    :errors="errors"
    :readonly="readonly"
    @expand-toggle="onToggle"
    @remove="onRemove"
    @select-item="onSelectItem"
    @add="emit('add')"
  />
  <CommissionCountriesMobileTabs
    v-else
    :tabs="tabs"
    :errors="errors"
    :readonly="readonly"
    @expand-toggle="onToggle"
    @remove="onRemove"
    @select="onSelectItem"
    @add="emit('add')"
  />
</template>
